/**
 * Climbing a ladder: which one the player is at, where it holds them, and how far up it
 * they get this frame.
 *
 * The whole control is the movement axis the player already has — push toward the ladder
 * to go up, away to come down. That costs no new key on a keyboard and, more to the
 * point, no new button under a thumb on a phone, which would be a permanent cost on
 * every HUD for a thing twenty spots in one profile use.
 *
 * Everything here is expressed in the ladder's own two axes: `into`, pointing at the
 * hedge it leans on, and the sideways axis along that hedge face. Pure and headless — the
 * player controller reads these answers and moves the body; no collider, no sensor, no
 * physics query.
 */

import {
  LADDER_DEPTH,
  LADDER_HEDGE_OFFSET,
  LADDER_WIDTH,
  type Ladder,
} from './maze/placeLadders';
import type { GroundPoint } from './profile/types';
import { PLAYER_CAPSULE_RADIUS } from './profile/validate';

/** Deliberately below WALK_SPEED: a ladder is a detour with a price, not a shortcut. */
export const CLIMB_SPEED = 2;

/** How far out from the ladder's centre the player is held while on it. */
export const LADDER_STANDOFF = LADDER_DEPTH / 2 + PLAYER_CAPSULE_RADIUS + 0.05;

/** How far out from it they can still be and take hold — about half a stride. */
export const LADDER_REACH = LADDER_STANDOFF + 0.35;

/** How far along the hedge face they can be: the ladder's own width, plus the capsule. */
export const LADDER_HALF_SPAN = LADDER_WIDTH / 2 + PLAYER_CAPSULE_RADIUS;

/**
 * Both thresholds compare against the *direction* the player is pushing, not the distance
 * they would have moved, so they mean the same thing at 30 fps as at 120.
 *
 * Taking hold needs a firmer push than staying on: within 60° of the ladder to grab it,
 * so walking diagonally past one never snatches the player onto it, but within 78° to
 * keep climbing, so a hand drifting on a touch stick does not stall the climb.
 */
export const CLIMB_DEADZONE = 0.2;
export const ATTACH_PUSH = 0.5;

/** The axis along the hedge face, `into` turned a quarter turn. */
function sideOf(into: GroundPoint): GroundPoint {
  return { x: into.z, z: -into.x };
}

/** How far the point lies out from the ladder, against `into`. */
function outFrom(ladder: Ladder, point: GroundPoint): number {
  return -((point.x - ladder.base.x) * ladder.into.x + (point.z - ladder.base.z) * ladder.into.z);
}

/** How far the point lies along the hedge face from the ladder's centre line. */
function alongFrom(ladder: Ladder, point: GroundPoint): number {
  const side = sideOf(ladder.into);
  return (point.x - ladder.base.x) * side.x + (point.z - ladder.base.z) * side.z;
}

/**
 * The ladder the player at `feet` could take hold of, or null. The nearest one wins, so
 * two ladders that ended up close together on a small maze still resolve to one answer.
 */
export function ladderAt(feet: GroundPoint, ladders: readonly Ladder[]): Ladder | null {
  let best: Ladder | null = null;
  let bestDistance = Infinity;

  for (const ladder of ladders) {
    const out = outFrom(ladder, feet);
    // Out of reach, or on the far side of the hedge it leans on.
    if (out < 0 || out > LADDER_REACH) continue;
    const along = alongFrom(ladder, feet);
    if (Math.abs(along) > LADDER_HALF_SPAN) continue;

    const distance = Math.hypot(out, along);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = ladder;
    }
  }

  return best;
}

/**
 * Where the player is held while on the ladder: squarely in front of it, a capsule clear
 * of the hedge face, with whatever sideways offset they arrived with clamped onto the
 * rails. Keeping that offset rather than centring them is what stops the grab from
 * lurching the view sideways.
 */
export function ladderAnchor(ladder: Ladder, feet: GroundPoint): GroundPoint {
  const half = LADDER_WIDTH / 2;
  const along = Math.max(-half, Math.min(half, alongFrom(ladder, feet)));
  const side = sideOf(ladder.into);
  return {
    x: ladder.base.x + side.x * along - ladder.into.x * LADDER_STANDOFF,
    z: ladder.base.z + side.z * along - ladder.into.z * LADDER_STANDOFF,
  };
}

/**
 * Where a player who has climbed to the top and kept pushing ends up: on the middle of
 * the hedge's crest, rather than in the air beside it where the ladder held them. The
 * hedge collider is one solid box with a flat top, so from here they can walk it.
 */
export function ladderCrest(ladder: Ladder): GroundPoint {
  return {
    x: ladder.base.x + ladder.into.x * LADDER_HEDGE_OFFSET,
    z: ladder.base.z + ladder.into.z * LADDER_HEDGE_OFFSET,
  };
}

/**
 * Up, down or still, from how squarely the player is pushing at the ladder.
 * `intoLadder` is the dot product of their movement direction with `into`, so it is
 * 1 pushing straight at the hedge and -1 pushing straight away from it.
 */
export function climbDirection(intoLadder: number): -1 | 0 | 1 {
  if (intoLadder > CLIMB_DEADZONE) return 1;
  if (intoLadder < -CLIMB_DEADZONE) return -1;
  return 0;
}

/** The player's feet after this frame, clamped to the ladder's two ends. */
export function climbStep(
  ladder: Ladder,
  feetY: number,
  intoLadder: number,
  delta: number,
): number {
  const moved = feetY + climbDirection(intoLadder) * CLIMB_SPEED * delta;
  return Math.max(0, Math.min(ladder.height, moved));
}
